import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import CdCSelector from '../components/CdCSelector';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

interface CommessaOption {
  id: string;
  codice: string;
  nome: string;
}

const NuovaImputazione: React.FC = () => {
  const navigate = useNavigate();

  const [centroCostoId, setCentroCostoId] = useState<string | null>(null);
  const [commessaId, setCommessaId] = useState('');
  const [importo, setImporto] = useState('');
  const [direzione, setDirezione] = useState<'dare' | 'avere'>('dare');
  const [dataCompetenza, setDataCompetenza] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [note, setNote] = useState('');

  const [commesse, setCommesse] = useState<CommessaOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load commesse for select
  useEffect(() => {
    fetch('/api/v1/centri-costo/commesse?limit=200&stato=aperta', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: CommessaOption[]) => setCommesse(data))
      .catch(() => {});
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!centroCostoId) {
      setError('Il centro di costo è obbligatorio');
      return;
    }
    if (!importo || isNaN(parseFloat(importo))) {
      setError("Importo non valido");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        centro_costo_id: centroCostoId,
        importo: parseFloat(importo),
        direzione,
        data_competenza: dataCompetenza,
      };
      if (commessaId) payload.commessa_id = commessaId;
      if (note.trim()) payload.note = note.trim();

      const res = await fetch('/api/v1/centri-costo/imputazioni', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore creazione: ${res.status}`);
      }
      navigate('/centri-costo/imputazioni');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [centroCostoId, commessaId, importo, direzione, dataCompetenza, note, navigate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="rounded-md p-1.5 hover:bg-gray-100"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <h1 className="text-2xl font-semibold">Nuova imputazione</h1>
      </div>

      {/* Form */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4 max-w-lg">
        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Centro di costo <span className="text-red-500">*</span>
          </label>
          <CdCSelector value={centroCostoId} onChange={setCentroCostoId} />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Commessa (opzionale)</label>
          <select
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={commessaId}
            onChange={(e) => setCommessaId(e.target.value)}
          >
            <option value="">Nessuna commessa</option>
            {commesse.map((c) => (
              <option key={c.id} value={c.id}>
                {c.codice} — {c.nome}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Importo (€) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Direzione</label>
          <div className="flex gap-4">
            {(['dare', 'avere'] as const).map((d) => (
              <label key={d} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="direzione"
                  value={d}
                  checked={direzione === d}
                  onChange={() => setDirezione(d)}
                  className="h-4 w-4 border-gray-300 text-blue-600"
                />
                <span className="text-sm text-gray-700 capitalize">{d}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Data competenza</label>
          <input
            type="date"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={dataCompetenza}
            onChange={(e) => setDataCompetenza(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
            onClick={() => navigate(-1)}
          >
            Annulla
          </button>
          <button
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving}
          >
            <Send className="h-4 w-4" />
            {saving ? 'Salvataggio…' : 'Crea imputazione'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NuovaImputazione;
